import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sun, ChevronRight, Check, X, Loader2, ArrowUpRight } from "lucide-react";

const EXPANSION_OPTIONS = [
  { key: "add_panels",       label: "Add more panels" },
  { key: "add_battery",      label: "Add battery backup" },
  { key: "add_ev",           label: "Add EV charger" },
  { key: "upgrade_inverter", label: "Upgrade inverter" },
  { key: "add_monitoring",   label: "Add monitoring / smart meter" },
  { key: "other",            label: "Something else" },
];

const STATUS_META = {
  new:        { label: "New request",      className: "bg-amber-100 text-amber-700" },
  reviewing:  { label: "PowerBuilt reviewing", className: "bg-sky-100 text-sky-700" },
  quoted:     { label: "Quote ready",      className: "bg-emerald-100 text-emerald-700" },
  converted:  { label: "In progress",      className: "bg-emerald-600 text-white" },
  declined:   { label: "Not proceeding",   className: "bg-slate-200 text-slate-600" },
};

export default function MySystems() {
  const [systems, setSystems] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanding, setExpanding] = useState(null);   // project object

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/clients/me/installed-systems"),
        api.get("/expansion-requests/mine"),
      ]);
      setSystems(s.data);
      setMyRequests(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openRequests = myRequests.filter((r) => !["converted", "declined"].includes(r.status));

  return (
    <div data-testid="my-systems">
      <p className="overline">My PowerBuilt</p>
      <h1 className="text-3xl sm:text-4xl font-heading font-medium text-[#0A2540] dark:text-white mt-2 mb-2">
        My systems
      </h1>
      <p className="text-sm text-[#475569] dark:text-slate-400 mb-6">
        Every installation we've done for you — request an upgrade at any time.
      </p>

      {loading ? (
        <div className="card-pb p-8 text-center text-sm text-[#475569]">Loading…</div>
      ) : systems.length === 0 ? (
        <div className="card-pb p-10 text-center">
          <Sun className="w-10 h-10 text-[#C9A356] mx-auto mb-3 opacity-60" />
          <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">No systems installed yet</h3>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            Once your first PowerBuilt system is installed, it will show up here.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {systems.map((p) => {
            const openReq = openRequests.find((r) => r.project_id === p.project_id);
            return (
              <article
                key={p.project_id}
                data-testid={`system-card-${p.project_id}`}
                className="card-pb p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4 text-[#C9A356]" />
                      <span className="text-[10px] uppercase tracking-wider font-bold text-[#C9A356]">
                        {p.project_type?.replace("_", " ") || "solar"}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg text-[#0A2540] dark:text-white mt-1">{p.title}</h3>
                    <div className="text-xs text-[#475569] dark:text-slate-400 mt-1">
                      {p.kw_target ? `${p.kw_target} kW · ` : ""}
                      Installed {p.install_date ? new Date(p.install_date).toLocaleDateString() : "recently"}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
                    Active
                  </span>
                </div>

                {openReq ? (
                  <div className="mt-4 rounded-md border border-sky-200 dark:border-sky-800/40 bg-sky-50/60 dark:bg-sky-900/10 p-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] uppercase font-bold tracking-wider text-sky-700 dark:text-sky-300">
                        Expansion in progress
                      </span>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_META[openReq.status]?.className || "bg-slate-100"}`}>
                        {STATUS_META[openReq.status]?.label || openReq.status}
                      </span>
                    </div>
                    <p className="text-xs text-[#0A2540] dark:text-white">
                      {openReq.types.map((t) => EXPANSION_OPTIONS.find((o) => o.key === t)?.label || t).join(" · ")}
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => setExpanding(p)}
                    data-testid={`request-expansion-${p.project_id}`}
                    className="mt-4 inline-flex items-center gap-1.5 w-full h-11 rounded-md bg-[#0A2540] hover:bg-[#12365b] text-white text-sm font-semibold justify-center transition"
                  >
                    <ArrowUpRight className="w-4 h-4" /> Request an upgrade
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* Past requests */}
      {myRequests.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading text-lg text-[#0A2540] dark:text-white mb-3">Your upgrade requests</h2>
          <ul className="space-y-2">
            {myRequests.map((r) => (
              <li
                key={r.request_id}
                data-testid={`my-expansion-${r.request_id}`}
                className="card-pb p-4 flex flex-wrap items-center justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-[#475569] dark:text-slate-400 mb-1">
                    {new Date(r.created_at).toLocaleDateString()} · {r.project_title}
                  </div>
                  <div className="text-sm text-[#0A2540] dark:text-white">
                    {r.types.map((t) => EXPANSION_OPTIONS.find((o) => o.key === t)?.label || t).join(" · ")}
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_META[r.status]?.className || "bg-slate-100"}`}>
                  {STATUS_META[r.status]?.label || r.status}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {expanding && (
        <ExpansionRequestModal
          system={expanding}
          onClose={() => setExpanding(null)}
          onSubmitted={() => { setExpanding(null); load(); }}
        />
      )}
    </div>
  );
}

function ExpansionRequestModal({ system, onClose, onSubmitted }) {
  const [types, setTypes] = useState([]);
  const [notes, setNotes] = useState("");
  const [budget, setBudget] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (k) => setTypes((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);

  const submit = async () => {
    if (types.length === 0) { toast.error("Choose at least one upgrade type"); return; }
    setSaving(true);
    try {
      await api.post("/expansion-requests", {
        project_id: system.project_id,
        types,
        notes: notes.trim(),
        budget: budget ? parseFloat(budget) : null,
      });
      toast.success("Request sent — PowerBuilt will be in touch");
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true"
      data-testid="expansion-modal"
    >
      <div className="bg-white dark:bg-slate-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#E2E8F0] dark:border-slate-700">
          <div className="min-w-0">
            <p className="overline">Request an upgrade</p>
            <h3 className="font-heading text-lg text-[#0A2540] dark:text-white truncate">{system.title}</h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="expansion-close" className="w-9 h-9 grid place-items-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-[#475569]">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <section>
            <p className="text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-2">
              1. What would you like? (pick one or more)
            </p>
            <div className="grid grid-cols-2 gap-2">
              {EXPANSION_OPTIONS.map((o) => {
                const on = types.includes(o.key);
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => toggle(o.key)}
                    data-testid={`exp-opt-${o.key}`}
                    className={`h-14 rounded-lg border-2 text-left px-3 transition ${
                      on ? "border-[#C9A356] bg-[#C9A356]/10" : "border-[#E2E8F0] dark:border-slate-700 hover:border-[#C9A356]/60"
                    }`}
                  >
                    <div className="flex items-center gap-2 h-full">
                      <span className={`w-4 h-4 rounded grid place-items-center shrink-0 ${on ? "bg-[#C9A356] text-white" : "border-2 border-[#E2E8F0] dark:border-slate-600"}`}>
                        {on && <Check className="w-3 h-3" />}
                      </span>
                      <span className="text-sm font-medium text-[#0A2540] dark:text-white">{o.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <p className="text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-2">
              2. Tell us more (optional)
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Anything you want us to know — usage patterns, timing, preferences."
              data-testid="expansion-notes"
              className="w-full rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 py-2 text-sm"
            />
          </section>

          <section>
            <p className="text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-2">
              3. Budget (optional)
            </p>
            <input
              type="number" min="0" step="100"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="Approximate budget in USD"
              data-testid="expansion-budget"
              className="w-full rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 h-10 text-sm"
            />
          </section>
        </div>

        <footer className="p-5 border-t border-[#E2E8F0] dark:border-slate-700 flex flex-col-reverse sm:flex-row-reverse gap-2">
          <button
            onClick={submit}
            disabled={saving || types.length === 0}
            data-testid="expansion-submit"
            className="h-11 px-5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
            Send request
          </button>
          <button onClick={onClose} className="h-11 px-5 rounded-md border border-[#E2E8F0] dark:border-slate-700 text-sm">Cancel</button>
        </footer>
      </div>
    </div>
  );
}
